import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import {
  AlertCircle,
  Bell,
  CalendarCheck,
  CalendarOff,
  CheckCircle2,
  ChevronRight,
  Copy,
  HelpCircle,
  Mail,
  RefreshCw,
  Scale,
  Thermometer,
  Wallet,
  X,
} from 'lucide-react';
import { api, type AfwijkingenData, type PeriodeVerschil, type UrencontroleData, type UrencontroleDag, type UrencontroleStatus } from '../lib/api';
import { cn, formatDate, formatHours, formatMoney } from '../lib/utils';
import { afwijkingDetails, afwijkingInfo } from '../components/AfwijkingPanel';
import {
  Card,
  Disclosure,
  EmptyState,
  ICON,
  IconButton,
  Kpi,
  ListRow,
  Notice,
  PageHeader,
  PageSkeleton,
  Segmented,
  Sheet,
  Tabs,
} from '../components/ui';
import { PeriodeVergelijking } from './Compare';

type Tab = 'periodes' | 'urencontrole' | 'eigen' | 'meldingen';
type Eigen = AfwijkingenData['eigen'][number];

const TABS: Tab[] = ['periodes', 'urencontrole', 'eigen', 'meldingen'];
const capFirst = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
const signedMoney = (n: number) => `${n > 0 ? '+' : ''}${formatMoney(n)}`;
const inPeriode = (date: string, p: PeriodeVerschil) => date >= p.start_date && date <= p.end_date;

/**
 * Loon controleren: per loonperiode de app naast loonstrook en urenbijlage, je eigen meldingen per
 * dienst, en per periode een mail aan de planner (die je zelf verstuurt).
 */
export function AfwijkingenPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['afwijkingen'],
    queryFn: api.afwijkingen.get,
    staleTime: 10 * 60_000,
  });
  const uren = useQuery({
    queryKey: ['afwijkingen', 'urencontroles'],
    queryFn: api.afwijkingen.urencontroles,
    staleTime: 10 * 60_000,
  });
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab') as Tab | null;
  const tab: Tab = tabParam && TABS.includes(tabParam) ? tabParam : 'periodes';
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(params);
    if (t === 'periodes') next.delete('tab');
    else next.set('tab', t);
    setParams(next, { replace: true });
  };

  if (error) {
    return (
      <Card>
        <EmptyState icon={AlertCircle} title="De controle kon niet laden" text={(error as Error).message} />
      </Card>
    );
  }
  if (isLoading || !data) return <PageSkeleton kpis={3} />;

  return (
    <div className="page">
      <PageHeader title="Loon controleren" sub="Klopt wat Trigion betaalt met wat je gewerkt hebt?" />

      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          {
            value: 'periodes',
            label: 'Periodes',
            count: data.counts.verschillen,
          },
          {
            value: 'urencontrole',
            label: 'Urencontrole',
            count: uren.data?.open,
          },
          {
            value: 'eigen',
            label: 'Mijn afwijkingen',
            count: data.counts.eigen,
          },
          {
            value: 'meldingen',
            label: 'Meldingen',
            count: data.counts.meldingen,
          },
        ]}
      />

      {tab === 'periodes' ? (
        <PeriodesTab data={data} />
      ) : tab === 'urencontrole' ? (
        <UrencontroleTab query={uren} />
      ) : tab === 'eigen' ? (
        <EigenTab data={data} />
      ) : (
        <MeldingenTab data={data} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Periodes                                                            */
/* ------------------------------------------------------------------ */

function StatusLabel({ p }: { p: PeriodeVerschil }) {
  if (p.status === 'ok') return <span className="status-ok">Klopt</span>;
  if (p.status === 'afwijking') return <span className="status-warn">Wijkt af</span>;
  return <span className="status-muted">Loonstrook nog niet binnen</span>;
}

function Verschil({ p }: { p: PeriodeVerschil }) {
  if (p.verschil == null) return <span className="text-slate-500">–</span>;
  return (
    <span className={cn('t-num', p.status === 'afwijking' ? 'font-medium text-amber-200' : 'text-slate-400')}>
      {signedMoney(p.verschil)}
    </span>
  );
}

function PeriodesTab({ data }: { data: AfwijkingenData }) {
  const [open, setOpen] = useState<string | null>(null);
  const periodes = data.periodes; // nieuwste eerst
  const today = format(new Date(), 'yyyy-MM-dd');

  const gecontroleerd = periodes.filter((p) => p.status !== 'geen_loonstrook');
  const ok = gecontroleerd.filter((p) => p.status === 'ok').length;
  const laatste = gecontroleerd[0];
  const volgende = periodes.filter((p) => p.payday && p.payday >= today).sort((a, b) => a.payday!.localeCompare(b.payday!))[0];
  const dagenTot = volgende?.payday ? differenceInCalendarDays(parseISO(volgende.payday), parseISO(today)) : null;

  const selected = periodes.find((p) => `${p.year}-${p.period}` === open) ?? null;

  if (!periodes.length) {
    return (
      <Card>
        <EmptyState
          icon={Scale}
          title="Nog geen loonperiodes"
          text="Zodra er diensten en een loonstrook zijn, zie je hier per periode of het klopt."
        />
      </Card>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
        <Kpi
          icon={CheckCircle2}
          label="Periodes die kloppen"
          text={gecontroleerd.length ? `${ok} van ${gecontroleerd.length}` : '–'}
          tone={gecontroleerd.length > 0 && ok === gecontroleerd.length ? 'money' : 'default'}
          hint={gecontroleerd.length - ok > 0 ? `${gecontroleerd.length - ok} wijken af` : 'Met loonstrook'}
        />
        <Kpi
          icon={Scale}
          label="Verschil laatste loonstrook"
          value={laatste?.verschil ?? null}
          format={signedMoney}
          tone={laatste?.status === 'afwijking' ? 'warn' : laatste ? 'money' : 'default'}
          hint={laatste ? undefined : 'Nog geen loonstrook'}
          rows={
            laatste
              ? [
                  ['Periode', `P${laatste.period}`],
                  ['Berekening', 'app min Trigion'],
                ]
              : undefined
          }
        />
        <Kpi
          icon={Wallet}
          label="Volgende loonstrook"
          className="col-span-2 lg:col-span-1"
          text={volgende?.payday ? formatDate(volgende.payday) : '–'}
          hint={volgende ? undefined : 'Betaaldag onbekend'}
          rows={
            volgende
              ? [
                  ['Periode', `P${volgende.period}`],
                  ['Wanneer', dagenTot === 0 ? 'vandaag' : dagenTot === 1 ? 'morgen' : `over ${dagenTot} dagen`],
                ]
              : undefined
          }
        />
      </div>

      <Card flush title="Per periode">
        {/* Desktop: tabel */}
        <table className="table hidden sm:table">
          <thead>
            <tr>
              <th>Periode</th>
              <th>Datums</th>
              <th>Betaaldag</th>
              <th>Status</th>
              <th className="r">Verschil</th>
              <th className="w-10" aria-hidden />
            </tr>
          </thead>
          <tbody>
            {periodes.map((p) => {
              const key = `${p.year}-${p.period}`;
              return (
                <tr
                  key={key}
                  tabIndex={0}
                  role="button"
                  aria-label={`Periode ${p.period} ${p.year} openen`}
                  onClick={() => setOpen(key)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setOpen(key))}
                  className="cursor-pointer transition-colors duration-fast ease-calm hover:bg-surface-raised"
                >
                  <td className="font-medium text-slate-100">
                    Periode {p.period} <span className="font-normal text-slate-500">{p.year}</span>
                  </td>
                  <td className="t-num text-slate-300">
                    {formatDate(p.start_date, 'dd-MM')} t/m {formatDate(p.end_date)}
                  </td>
                  <td className="t-num text-slate-300">{p.payday ? formatDate(p.payday) : '–'}</td>
                  <td>
                    <StatusLabel p={p} />
                  </td>
                  <td className="r">
                    <Verschil p={p} />
                  </td>
                  <td className="!pl-0">
                    <ChevronRight className={cn(ICON.sm, 'text-slate-500')} aria-hidden />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Mobiel: rijen */}
        <div className="list border-t border-surface-line sm:hidden">
          {periodes.map((p, i) => (
            <ListRow
              key={`${p.year}-${p.period}`}
              i={i}
              title={
                <>
                  Periode {p.period} <span className="text-slate-500">{p.year}</span>
                </>
              }
              meta={
                <span className="flex items-center gap-2">
                  <StatusLabel p={p} />
                  <span className="t-num">
                    {formatDate(p.start_date, 'dd-MM')} t/m {formatDate(p.end_date, 'dd-MM')}
                  </span>
                </span>
              }
              value={<Verschil p={p} />}
              onClick={() => setOpen(`${p.year}-${p.period}`)}
            />
          ))}
        </div>
      </Card>

      <Card flush>
        <Disclosure icon={HelpCircle} title="Hoe werkt de controle?">
          <div className="space-y-2 t-muted">
            <p>
              Per loonperiode zet de app wat hij met de CAO berekent naast je loonstrook en urenbijlage. Verschillen onder € 10,00 gelden
              als afronding.
            </p>
            <p>Regels op de loonstrook die los staan van je diensten (zoals een uitkering) tellen niet mee in het verschil.</p>
            <p>
              Ging een dienst anders dan gepland? Meld het in de kalender bij die dienst. Dan rekent de app opnieuw en komt het in de mail
              aan de planner.
            </p>
          </div>
        </Disclosure>
      </Card>

      {selected && (
        <PeriodeSheet
          key={open!}
          p={selected}
          eigen={data.eigen.filter((e) => inPeriode(e.shift_date, selected))}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

/** Eén zin in gewone taal: wat betekent het verschil van deze periode? */
function conclusie(p: PeriodeVerschil): string {
  if (p.status === 'geen_loonstrook' || p.verschil == null) {
    return 'De loonstrook van deze periode is er nog niet. De app haalt hem op zodra hij in AFAS staat.';
  }
  const bedrag = formatMoney(Math.abs(p.verschil));
  if (p.status === 'ok') return `Trigion betaalde wat de app berekent (verschil ${bedrag}, afronding).`;
  if (p.verschil > 0) {
    return `De app berekent ${bedrag} meer dan Trigion betaalde. Als alle diensten zo gewerkt zijn, heb je mogelijk te weinig gekregen.`;
  }
  if (p.verschil < 0) {
    return `Trigion betaalde ${bedrag} meer dan de app berekent. Meestal mist de app iets, zoals verlof of een toeslag buiten je rooster.`;
  }
  return 'Het bedrag klopt, maar op sommige dagen verschillen de uren.';
}

type SheetDeel = 'overzicht' | 'verschillen' | 'per_dag';

function PeriodeSheet({ p, eigen, onClose }: { p: PeriodeVerschil; eigen: Eigen[]; onClose: () => void }) {
  const mailRef = useRef<HTMLDivElement>(null);
  const mail = useMutation({
    mutationFn: () => api.afwijkingen.mail(p.year, p.period),
  });
  const betaaldVoorDiensten = p.loonstrook_bruto != null ? p.loonstrook_bruto - p.bijzonder.reduce((s, b) => s + b.bedrag, 0) : null;
  const nVerschillen = p.dagen.length + eigen.length;
  const [deel, setDeel] = useState<SheetDeel>('overzicht');

  useEffect(() => {
    if (mail.data) {
      setDeel('overzicht');
      requestAnimationFrame(() => mailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }, [mail.data]);

  return (
    <Sheet
      open
      wide
      vasteHoogte
      onClose={onClose}
      title={`Periode ${p.period} ${p.year}`}
      sub={`${formatDate(p.start_date, 'dd-MM')} t/m ${formatDate(p.end_date)}${p.payday ? `, betaaldag ${formatDate(p.payday)}` : ''}`}
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="hidden text-xs text-slate-500 sm:block">De app verstuurt zelf niets.</span>
          <button type="button" className="btn-primary w-full sm:w-auto" onClick={() => mail.mutate()} disabled={mail.isPending}>
            <Mail className={ICON.sm} aria-hidden />
            {mail.isPending ? 'Mail maken…' : mail.data ? 'Mail opnieuw maken' : 'Mail aan planner maken'}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        <div
          className={cn(
            'rounded-xl border p-4',
            p.status === 'afwijking'
              ? 'border-amber-400/30 bg-amber-400/5'
              : p.status === 'ok'
                ? 'border-money/30 bg-money/5'
                : 'border-surface-line'
          )}
        >
          <StatusLabel p={p} />
          <p className="t-body mt-1.5">{conclusie(p)}</p>
        </div>

        <Segmented
          label="Onderdeel"
          value={deel}
          onChange={setDeel}
          items={[
            { value: 'overzicht', label: 'Overzicht' },
            {
              value: 'verschillen',
              label: nVerschillen ? `Verschillen (${nVerschillen})` : 'Verschillen',
            },
            { value: 'per_dag', label: 'Per dag' },
          ]}
        />

        {mail.error && <Notice tone="err">De mail kon niet worden gemaakt: {(mail.error as Error).message}</Notice>}

        {deel === 'overzicht' && (
          <div className="space-y-5">
            {mail.data && (
              <div ref={mailRef} className="scroll-mt-4">
                <MailBlok mail={mail.data} />
              </div>
            )}
            {p.loonstrook_bruto != null && betaaldVoorDiensten != null ? (
              <section className="space-y-3">
                <dl className="grid grid-cols-3 gap-3">
                  <Tegel label="App" value={formatMoney(p.app_bruto)} />
                  <Tegel label="Loonstrook" value={formatMoney(betaaldVoorDiensten)} />
                  <Tegel
                    label="Verschil"
                    value={p.verschil != null ? signedMoney(p.verschil) : '–'}
                    className={p.status === 'afwijking' ? 'text-amber-200' : 'text-money'}
                  />
                </dl>
                <dl className="divide-y divide-surface-line/60 rounded-xl border border-surface-line px-4 text-sm">
                  <Regel label="Bruto op je loonstrook" value={formatMoney(p.loonstrook_bruto)} />
                  {p.bijzonder.map((b) => (
                    <Regel key={b.omschrijving} label={`${b.omschrijving} (los van diensten)`} value={formatMoney(-b.bedrag)} muted />
                  ))}
                  {p.bijzonder.length > 0 && <Regel label="Betaald voor je diensten" value={formatMoney(betaaldVoorDiensten)} strong />}
                  <Regel label="Berekend door de app" value={formatMoney(p.app_bruto)} strong />
                </dl>
              </section>
            ) : (
              <dl className="divide-y divide-surface-line/60 rounded-xl border border-surface-line px-4 text-sm">
                <Regel label="Berekend door de app" value={formatMoney(p.app_bruto)} strong />
                <Regel label="Loonstrook" value="Nog niet binnen" muted />
              </dl>
            )}
          </div>
        )}

        {deel === 'verschillen' &&
          (nVerschillen === 0 ? (
            <p className="t-muted">Geen dagen met een ander aantal uren en geen eigen afwijkingen in deze periode.</p>
          ) : (
            <div className="space-y-5">
              {!!p.dagen.length && (
                <section className="space-y-2">
                  <div>
                    <h3>Dagen met ander aantal uren</h3>
                    <p className="t-muted">Klopt de app niet, pas dan de dienst aan in de kalender.</p>
                  </div>
                  <div className="divide-y divide-surface-line/60 rounded-xl border border-surface-line">
                    {p.dagen.map((d) => (
                      <div key={d.date} className="space-y-1 px-4 py-3">
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="text-sm font-medium text-slate-100">{capFirst(formatDate(d.date, 'EEEE dd-MM'))}</span>
                          <span className={cn('t-num text-sm font-medium', d.verschil > 0 ? 'text-amber-200' : 'text-slate-200')}>
                            {d.verschil > 0 ? '+' : ''}
                            {formatHours(d.verschil)}
                          </span>
                        </div>
                        <div className="grid gap-0.5 text-xs text-slate-400 sm:grid-cols-2">
                          <div className="min-w-0 truncate">
                            App <span className="t-num text-slate-300">{formatHours(d.app_uren)}</span>
                            {d.app ? `, ${d.app}` : ''}
                          </div>
                          <div className="min-w-0 truncate">
                            Trigion <span className="t-num text-slate-300">{formatHours(d.trigion_uren)}</span>
                            {d.trigion ? `, ${d.trigion}` : ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
              {!!eigen.length && (
                <section className="space-y-2">
                  <h3>Jouw afwijkingen</h3>
                  <div className="list overflow-hidden rounded-xl border border-surface-line">
                    {eigen.map((e, i) => (
                      <EigenRow key={e.id} e={e} i={i} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          ))}

        {deel === 'per_dag' && (
          <section className="space-y-2">
            <p className="t-muted">Jouw diensten naast de uren van Trigion.</p>
            <PeriodeVergelijking year={p.year} period={p.period} />
          </section>
        )}
      </div>
    </Sheet>
  );
}

function Regel({ label, value, muted, strong }: { label: string; value: string; muted?: boolean; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2.5">
      <dt className={cn(muted ? 'text-slate-400' : strong ? 'font-medium text-slate-100' : 'text-slate-300')}>{label}</dt>
      <dd className={cn('t-num', muted ? 'text-slate-400' : strong ? 'font-medium text-slate-50' : 'text-slate-200')}>{value}</dd>
    </div>
  );
}

function Tegel({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className="min-w-0 rounded-lg border border-surface-line p-3">
      <dt className="t-label">{label}</dt>
      <dd className={cn('t-num mt-1 truncate text-base font-semibold text-slate-50', className)}>{value}</dd>
    </div>
  );
}

function MailBlok({
  mail,
}: {
  mail: {
    subject: string;
    body: string;
    meldingen: number;
    dagen: number;
    leeg: boolean;
  };
}) {
  const [copied, setCopied] = useState<'ok' | 'fout' | null>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(mail.body);
      setCopied('ok');
    } catch {
      setCopied('fout');
    }
  };
  const mailto = `mailto:?subject=${encodeURIComponent(mail.subject)}&body=${encodeURIComponent(mail.body)}`;
  const inhoud = [
    mail.meldingen ? `${mail.meldingen} ${mail.meldingen === 1 ? 'melding' : 'meldingen'}` : null,
    mail.dagen ? `${mail.dagen} ${mail.dagen === 1 ? 'dag' : 'dagen'} met verschil` : null,
  ].filter(Boolean);

  return (
    <section className="space-y-3 rounded-lg border border-surface-line p-3 sm:p-4">
      <div>
        <h3>Mail aan planner</h3>
        <p className="t-muted">{inhoud.length ? `Met ${inhoud.join(' en ')}. ` : ''}De app verstuurt zelf niets.</p>
      </div>
      {mail.leeg && <Notice tone="info">Er is in deze periode niets om te melden.</Notice>}
      <div>
        <label className="label" htmlFor="mail-subject">
          Onderwerp
        </label>
        <input id="mail-subject" className="input" readOnly value={mail.subject} onFocus={(e) => e.currentTarget.select()} />
      </div>
      <div>
        <label className="label" htmlFor="mail-body">
          Tekst
        </label>
        <textarea id="mail-body" className="input" readOnly rows={10} value={mail.body} />
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-secondary" onClick={copy}>
          <Copy className={ICON.sm} aria-hidden /> Kopiëren
        </button>
        <a className="btn-secondary" href={mailto}>
          <Mail className={ICON.sm} aria-hidden /> Openen in mail-app
        </a>
      </div>
      {copied === 'ok' && <Notice tone="ok">Tekst gekopieerd.</Notice>}
      {copied === 'fout' && <Notice tone="err">Kopiëren lukte niet. Selecteer de tekst en kopieer hem zelf.</Notice>}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Urencontrole                                                        */
/* ------------------------------------------------------------------ */

type Bron = 'rooster' | 'app' | 'urencontrole';

const DAG_STATUS: Record<UrencontroleStatus, { label: string; uitleg: string; afwijkend: Bron[] }> = {
  klopt: { label: 'Klopt', uitleg: '', afwijkend: [] },
  urencontrole_wijkt_af: {
    label: 'Urencontrole wijkt af',
    uitleg: 'Rooster en app zijn gelijk. Trigion heeft dit anders in de urencontrole gezet: meld het bij de planner.',
    afwijkend: ['urencontrole'],
  },
  app_wijkt_af: {
    label: 'App wijkt af',
    uitleg: 'Rooster en urencontrole zijn gelijk. Pas de dienst in de app aan.',
    afwijkend: ['app'],
  },
  rooster_wijkt_af: {
    label: 'Rooster wijkt af',
    uitleg: 'App en urencontrole zijn gelijk, maar het rooster zegt iets anders.',
    afwijkend: ['rooster'],
  },
  alle_verschillen: {
    label: 'Alle drie verschillen',
    uitleg: 'Rooster, app en urencontrole hebben alle drie een ander aantal uren.',
    afwijkend: ['rooster', 'app', 'urencontrole'],
  },
  rooster_app_verschillen: {
    label: 'Rooster en app verschillen',
    uitleg: 'Van deze periode is geen urencontrole of urenbijlage, dus alleen rooster en app zijn vergeleken. Ze hebben een ander aantal uren.',
    afwijkend: ['rooster', 'app'],
  },
  app_urencontrole_verschillen: {
    label: 'App en urencontrole verschillen',
    uitleg: 'Voor deze dag is geen rooster gevonden, dus alleen app en urencontrole zijn vergeleken.',
    afwijkend: ['app', 'urencontrole'],
  },
};

function BronKolom({ naam, uren, regels, afwijkend, geen }: { naam: string; uren: number | null; regels: string[]; afwijkend: boolean; geen: string }) {
  return (
    <div className="min-w-0">
      <div className="t-label">{naam}</div>
      <div className={cn('t-num mt-0.5 text-sm font-semibold', uren == null ? 'text-slate-500' : afwijkend ? 'text-amber-200' : 'text-slate-100')}>
        {uren == null ? geen : formatHours(uren)}
      </div>
      {uren != null && <div className="mt-0.5 break-words text-xs text-slate-500">{regels.length ? regels.join(', ') : 'geen dienst'}</div>}
    </div>
  );
}

/** Label en uitleg per dag; op een ziektedag spreekt de uitleg over de ziekmelding (AFAS) in plaats van het rooster. */
function dagUitleg(d: UrencontroleDag): { label: string; uitleg: string; afwijkend: Bron[] } {
  const st = DAG_STATUS[d.status];
  if (!d.ziek) return st;
  if (d.status === 'klopt') {
    return {
      label: 'Klopt (ziek)',
      uitleg: `${d.ziek}. Trigion zet ziekte-uren in de urencontrole zonder tijden; app en urencontrole zijn gelijk. Het rooster toont een ziekgemelde dag niet.`,
      afwijkend: [],
    };
  }
  if (d.app === 0 && (d.urencontrole ?? 0) > 0) {
    return { label: 'App wijkt af', uitleg: `${d.ziek}, maar de app heeft op deze dag geen ziekte-uren. Meld de dag ziek in de kalender.`, afwijkend: ['app'] };
  }
  return { label: st.label, uitleg: `${d.ziek}. App en urencontrole hebben een ander aantal uren.`, afwijkend: ['app', 'urencontrole'] };
}

function UrencontroleDagRij({ d, derde }: { d: UrencontroleDag; derde: 'urencontrole' | 'urenbijlage' | null }) {
  const st = dagUitleg(d);
  return (
    <div className="space-y-2 px-4 py-3 sm:px-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <span className={cn('text-sm', st.afwijkend.length ? 'font-medium text-slate-50' : 'text-slate-300')}>{capFirst(formatDate(d.date, 'EEEE dd-MM'))}</span>
        <span className={st.afwijkend.length ? 'status-warn' : 'status-ok'}>{st.label}</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <BronKolom naam="Rooster" uren={d.rooster} regels={d.rooster_regels} afwijkend={st.afwijkend.includes('rooster')} geen="geen rooster gevonden" />
        <BronKolom naam="App" uren={d.app} regels={d.app_regels} afwijkend={st.afwijkend.includes('app')} geen="–" />
        <BronKolom
          naam={derde === 'urenbijlage' ? 'Urenbijlage' : 'Urencontrole'}
          uren={d.urencontrole}
          regels={d.urencontrole_regels}
          afwijkend={st.afwijkend.includes('urencontrole')}
          geen="geen urencontrole of urenbijlage"
        />
      </div>
      {st.uitleg && <p className="t-muted">{st.uitleg}</p>}
      {d.verklaring.length > 0 && <p className="text-xs text-slate-500">Gekoppeld: {d.verklaring.join(' · ')}</p>}
    </div>
  );
}

function UrencontroleTab({ query }: { query: { data?: UrencontroleData; isLoading: boolean; error: unknown } }) {
  const { data, isLoading, error } = query;
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [alleDagen, setAlleDagen] = useState<Record<string, boolean>>({});
  const check = useMutation({
    mutationFn: async () => {
      // 1. Nieuwe mail ophalen. 2. Rooster bijwerken: de feed van nu, daarna voorbije weken (één per verzoek)
      // tot er niets meer te halen is. 3. De vergelijking opnieuw laten maken.
      const mail = await api.afwijkingen.urencontrolesCheck();
      let feedFout: string | null = null;
      for (let i = 0; i < 40; i++) {
        const r = await api.afwijkingen.urencontrolesRooster(i === 0);
        if (i === 0) feedFout = r.feed_fout;
        if (r.over === 0 || r.gedaan === 0) break;
      }
      const nieuw = await api.afwijkingen.urencontroles();
      return { mail, nieuw, feedFout };
    },
    onSuccess: ({ nieuw }) => {
      qc.setQueryData<UrencontroleData>(['afwijkingen', 'urencontroles'], nieuw);
      const vandaag = format(new Date(), 'yyyy-MM-dd');
      const nu = nieuw.urencontroles.find((u) => u.start_date <= vandaag && vandaag <= u.end_date);
      if (nu) setOpen(`${nu.year}-${nu.period}`);
    },
  });

  if (error) {
    return (
      <Card>
        <EmptyState icon={AlertCircle} title="De urencontrole kon niet laden" text={(error as Error).message} />
      </Card>
    );
  }
  if (isLoading || !data) return <PageSkeleton kpis={0} />;

  const vandaag = format(new Date(), 'yyyy-MM-dd');
  const huidigePeriode = data.urencontroles.find((u) => u.start_date <= vandaag && vandaag <= u.end_date);
  const uitslag = check.data;
  const aantalVerschil = (u: { dagen: UrencontroleDag[] }) => u.dagen.filter((d) => d.status !== 'klopt').length;
  const dagenZonderRooster = data.urencontroles.reduce((n, u) => n + u.dagen.filter((d) => d.rooster == null).length, 0);
  const geenRooster = dagenZonderRooster > 0;

  const kop = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="t-muted">
        Per dag de uren volgens je rooster, de app en de urencontrole van Trigion (of de urenbijlage uit AFAS) naast elkaar. Waar ze niet gelijk zijn, staat erbij welke afwijkt.
      </p>
      <button type="button" className="btn-secondary shrink-0" onClick={() => check.mutate()} disabled={check.isPending}>
        <RefreshCw className={cn(ICON.sm, check.isPending && 'animate-spin')} aria-hidden />
        {check.isPending ? 'Controleren…' : 'Check huidige periode'}
      </button>
    </div>
  );
  const meldingen = (
    <>
      {check.error && <Notice tone="err">De controle kon niet worden gedaan: {(check.error as Error).message}</Notice>}
      {uitslag && !check.isPending && (
        <Notice tone={uitslag.mail.mail_overgeslagen ? 'info' : 'ok'}>
          {uitslag.mail.mail_overgeslagen
            ? 'Je mail kon nu niet worden opgehaald, daarom is alleen opnieuw vergeleken. '
            : uitslag.mail.nieuwe_urencontroles
              ? 'Er was een nieuwe urencontrole in je mail; die is ingelezen. '
              : 'Er is geen nieuwe urencontrole in je mail. '}
          {huidigePeriode
            ? `Periode ${huidigePeriode.period} is opnieuw vergeleken: ${
                aantalVerschil(huidigePeriode) ? `${aantalVerschil(huidigePeriode)} dag(en) met een verschil.` : 'alle dagen komen overeen.'
              }`
            : 'Voor de huidige periode is nog geen urencontrole binnen.'}
          {uitslag.mail.nog_te_verwerken > 0 && ` Nog ${uitslag.mail.nog_te_verwerken} mail(s) te verwerken: druk nog eens.`}
        </Notice>
      )}
      {uitslag?.feedFout && <Notice tone="err">Het rooster van nu kon niet worden opgehaald: {uitslag.feedFout}.</Notice>}
      {data.rooster_te_halen > 0 ? (
        <Notice tone="info">
          Er is nog rooster op te halen ({data.rooster_te_halen} {data.rooster_te_halen === 1 ? 'stap' : 'stappen'}). Druk op
          Check huidige periode; dat doet de app dan in één keer.
        </Notice>
      ) : (
        geenRooster && (
          <Notice tone="info">
            Voor {dagenZonderRooster} {dagenZonderRooster === 1 ? 'dag' : 'dagen'} is geen rooster gevonden: er is geen bewaarde feed en geen rooster-PDF van Trigion.
            Die dagen zijn alleen tussen app en urencontrole of urenbijlage vergeleken.
          </Notice>
        )
      )}
    </>
  );

  if (!data.urencontroles.length) {
    return (
      <div className="flex flex-col gap-5">
        {kop}
        {meldingen}
        <Card>
          <EmptyState
            icon={Mail}
            title="Nog geen urencontrole"
            text="Trigion mailt de urencontrole als PDF. Zodra hij binnen is, zie je hier per dag of hij klopt met je rooster en de app."
          />
        </Card>
      </div>
    );
  }
  const eerste = `${data.urencontroles[0].year}-${data.urencontroles[0].period}`;
  // Eén tegelijk open: de nieuwste, tenzij je zelf iets anders opent of alles dichtklapt ('').
  const huidig = open ?? eerste;

  return (
    <div className="flex flex-col gap-5">
      {kop}
      {meldingen}
      {data.urencontroles.map((u) => {
        const key = `${u.year}-${u.period}`;
        const isOpen = huidig === key;
        const afwijkend = u.dagen.filter((d) => d.status !== 'klopt');
        const aantalMelding = afwijkend.length + (u.loon_verschil != null ? 1 : 0);
        const alle = alleDagen[key] ?? afwijkend.length === 0;
        const dagen = alle ? u.dagen : afwijkend;
        const derdeNaam = u.derde === 'urenbijlage' ? 'Urenbijlage' : 'Urencontrole';
        const verschilUren = u.urencontrole_uren == null ? null : Math.round((u.app_uren - u.urencontrole_uren) * 100) / 100;
        const kopKlopt = u.urencontrole_totaal == null || u.urencontrole_uren == null || Math.abs(u.urencontrole_totaal - u.urencontrole_uren) < 0.05;
        return (
          <Card
            key={key}
            flush
            className={cn('overflow-hidden transition-colors duration-fast ease-calm', isOpen ? 'border-brand-400/60 bg-surface-raised' : 'hover:border-slate-600')}
          >
            <Disclosure
              open={isOpen}
              onToggle={(v) => setOpen(v ? key : '')}
              className={cn('border-l-2 transition-colors duration-fast ease-calm', isOpen ? 'border-brand-400' : 'border-transparent')}
              title={`Periode ${u.period} ${u.year}`}
              meta={`${formatDate(u.start_date, 'dd-MM')} t/m ${formatDate(u.end_date)}${u.ingelezen_op ? `, ingelezen ${formatDate(u.ingelezen_op.replace(' ', 'T'), 'dd-MM')}` : ''}`}
              right={
                aantalMelding ? <span className="badge-count">{aantalMelding}</span> : u.derde ? <span className="status-ok">Klopt</span> : <span className="status-muted">Alleen rooster en app</span>
              }
            >
              <div className="-mx-4 border-t border-surface-line sm:-mx-5">
                <div className="space-y-3 px-4 py-3 sm:px-5">
                  <p className="t-body">
                    {afwijkend.length === 0
                      ? !u.derde
                        ? 'Van deze periode is geen urencontrole of urenbijlage. Rooster en app komen op elke dag overeen.'
                        : u.dagen.some((d) => d.rooster != null)
                        ? `Rooster, app en ${derdeNaam.toLowerCase()} komen op elke dag overeen.`
                        : `App en ${derdeNaam.toLowerCase()} komen op elke dag overeen. Het rooster is ${data.rooster_te_halen > 0 ? 'nog niet opgehaald' : 'voor deze periode niet beschikbaar'}.`
                      : `${afwijkend.length} ${afwijkend.length === 1 ? 'dag' : 'dagen'} waarop de bronnen niet gelijk zijn.`}
                  </p>
                  {(u.loonstrook || u.urenbijlage_uren != null) && (
                    <p className="text-xs text-slate-500">
                      Gekoppeld aan deze periode:
                      {u.loonstrook ? ` loonstrook ${formatHours(u.loonstrook.uren)} verloond, bruto ${formatMoney(u.loonstrook.bruto)}` : ''}
                      {u.loonstrook && u.urenbijlage_uren != null ? ' ·' : ''}
                      {u.urenbijlage_uren != null ? ` urenbijlage ${formatHours(u.urenbijlage_uren)}` : ''}
                    </p>
                  )}
                  {u.derde && u.urencontrole_uren != null && verschilUren != null && (
                    <dl className="grid grid-cols-3 gap-3">
                      <Tegel label={derdeNaam} value={formatHours(u.urencontrole_uren)} />
                      <Tegel label="App" value={formatHours(u.app_uren)} />
                      <Tegel
                        label="Verschil"
                        value={`${verschilUren > 0 ? '+' : ''}${formatHours(verschilUren)}`}
                        className={Math.abs(verschilUren) > 0.01 ? 'text-amber-200' : 'text-money'}
                      />
                    </dl>
                  )}
                  {u.loon_verschil != null && u.loonstrook && u.urenbijlage_uren != null && (
                    <Notice tone="info">
                      De loonstrook verloont {formatHours(u.loonstrook.uren)}, de urenbijlage uit AFAS {formatHours(u.urenbijlage_uren)}: {formatHours(Math.abs(u.loon_verschil))}{' '}
                      {u.loon_verschil < 0 ? 'minder' : 'meer'} betaald dan gewerkt. Controleer dit bij de planner of bij salarisadministratie.
                    </Notice>
                  )}
                  {!kopKlopt && (
                    <Notice tone="info">
                      De urencontrole zelf zegt {formatHours(u.urencontrole_totaal!)} in totaal, maar de ingelezen regels tellen {formatHours(u.urencontrole_uren ?? 0)}.
                      Mogelijk is een regel verkeerd ingelezen.
                    </Notice>
                  )}
                  {afwijkend.length > 0 && (
                    <Segmented
                      label="Dagen"
                      value={alle ? 'alle' : 'verschillen'}
                      onChange={(v) => setAlleDagen((prev) => ({ ...prev, [key]: v === 'alle' }))}
                      items={[
                        { value: 'verschillen', label: `Verschillen (${afwijkend.length})` },
                        { value: 'alle', label: 'Alle dagen' },
                      ]}
                    />
                  )}
                </div>
                <div className="divide-y divide-surface-line/60 border-t border-surface-line">
                  {dagen.map((d) => (
                    <UrencontroleDagRij key={d.date} d={d} derde={u.derde} />
                  ))}
                </div>
              </div>
            </Disclosure>
          </Card>
        );
      })}
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Mijn afwijkingen                                                    */
/* ------------------------------------------------------------------ */

function EigenRow({ e, i }: { e: Eigen; i?: number }) {
  const info = afwijkingInfo(e.deviation_reason);
  const ziek = e.leave_type === 'ziekte';
  const label = info?.label ?? (ziek ? 'Ziek (rooster)' : 'Verlof (rooster)');
  const meta = [label, ...afwijkingDetails(e), e.deviation_notes].filter(Boolean).join(', ');
  return (
    <ListRow
      i={i}
      icon={info?.icon ?? (ziek ? Thermometer : CalendarOff)}
      iconClass="text-amber-200"
      title={
        <>
          {formatDate(e.shift_date)}{' '}
          <span className="t-num text-slate-300">
            {e.start_time}–{e.end_time}
          </span>
          {e.object_naam_snapshot && <span className="text-slate-400"> {e.object_naam_snapshot}</span>}
        </>
      }
      meta={meta}
      value={formatMoney(e.bruto)}
      to={`/kalender?shift=${e.id}`}
    />
  );
}

function EigenTab({ data }: { data: AfwijkingenData }) {
  const today = format(new Date(), 'yyyy-MM-dd');
  // Groeperen per loonperiode (nieuwste eerst); diensten buiten de getoonde periodes per maand.
  const groepen = useMemo(() => {
    const out: Array<{
      key: string;
      label: string;
      datums: string | null;
      huidig: boolean;
      items: Eigen[];
    }> = [];
    for (const e of data.eigen) {
      const p = data.periodes.find((x) => inPeriode(e.shift_date, x));
      const key = p ? `p-${p.year}-${p.period}` : `m-${e.shift_date.slice(0, 7)}`;
      let g = out.find((x) => x.key === key);
      if (!g) {
        out.push(
          (g = {
            key,
            label: p ? `Periode ${p.period} ${p.year}` : capFirst(formatDate(e.shift_date, 'MMMM yyyy')),
            datums: p ? `${formatDate(p.start_date, 'dd-MM')} t/m ${formatDate(p.end_date)}` : null,
            huidig: !!p && inPeriode(today, p),
            items: [],
          })
        );
      }
      g.items.push(e);
    }
    return out;
  }, [data, today]);
  // Eén tegelijk open: de huidige periode, anders de nieuwste.
  const [open, setOpen] = useState<string | null>(() => (groepen.find((g) => g.huidig) ?? groepen[0])?.key ?? null);

  if (!groepen.length) {
    return (
      <Card>
        <EmptyState
          icon={CalendarCheck}
          title="Geen afwijkingen"
          text="Alles is gewerkt zoals gepland. Ging een dienst anders? Meld het in de kalender bij die dienst."
          action={
            <Link to="/kalender" className="btn-secondary">
              Naar de kalender
            </Link>
          }
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {groepen.map((g) => {
        const isOpen = open === g.key;
        return (
          <Card
            key={g.key}
            flush
            className={cn('overflow-hidden transition-colors duration-fast ease-calm', isOpen ? 'border-brand-400/60 bg-surface-raised' : 'hover:border-slate-600')}
          >
            <Disclosure
              open={isOpen}
              onToggle={(v) => setOpen(v ? g.key : null)}
              className={cn('border-l-2 transition-colors duration-fast ease-calm', isOpen ? 'border-brand-400' : 'border-transparent')}
              title={
                <span className="flex items-center gap-2">
                  {g.label}
                  {g.huidig && <span className="rounded-full border border-surface-line px-2 py-0.5 text-xs text-slate-400">Huidig</span>}
                </span>
              }
              meta={g.datums ?? undefined}
              right={<span className="badge-count">{g.items.length}</span>}
            >
              <div className="list -mx-4 border-t border-surface-line sm:-mx-5">
                {g.items.map((e, i) => (
                  <EigenRow key={e.id} e={e} i={i} />
                ))}
              </div>
            </Disclosure>
          </Card>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Meldingen                                                           */
/* ------------------------------------------------------------------ */

function MeldingenTab({ data }: { data: AfwijkingenData }) {
  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['afwijkingen'] });
    qc.invalidateQueries({ queryKey: ['notifications'] });
  };
  const dismiss = useMutation({
    mutationFn: (id: number) => api.notifications.dismiss(id),
    onSuccess: refresh,
  });
  const markRead = useMutation({
    mutationFn: (id: number) => api.notifications.markRead(id),
    onSuccess: refresh,
  });

  if (!data.meldingen.length) {
    return (
      <Card>
        <EmptyState icon={Bell} title="Geen meldingen" text="Berichten uit AFAS, je mail en het rooster komen hier." />
      </Card>
    );
  }

  return (
    <Card flush title="Meldingen" action={<span className="t-label">Uit AFAS, mail en rooster</span>}>
      <div className="list border-t border-surface-line">
        {data.meldingen.map((m, i) => {
          const unread = !m.read_at;
          return (
            <div key={m.id} className="row enter items-start" style={{ '--i': i } as React.CSSProperties}>
              <span
                className={cn('mt-2 h-1.5 w-1.5 shrink-0 rounded-full', unread ? 'bg-brand-400' : 'bg-transparent')}
                aria-label={unread ? 'Ongelezen' : undefined}
              />
              <div className="min-w-0 flex-1">
                <div className={cn('text-sm', unread ? 'font-medium text-slate-50' : 'text-slate-200')}>{m.title}</div>
                {m.body && <div className="line-clamp-2 text-sm text-slate-400">{m.body}</div>}
                <div className="mt-1 flex items-center gap-3 text-xs text-slate-500">
                  <span className="t-num">{formatDate(m.created_at.replace(' ', 'T'), 'dd-MM-yyyy HH:mm')}</span>
                  {m.link && (
                    <Link
                      to={m.link}
                      onClick={() => unread && markRead.mutate(m.id)}
                      className="inline-flex items-center gap-0.5 text-slate-300 hover:text-slate-50"
                    >
                      Openen <ChevronRight className={ICON.sm} aria-hidden />
                    </Link>
                  )}
                  {unread && (
                    <button type="button" onClick={() => markRead.mutate(m.id)} className="text-slate-400 hover:text-slate-100">
                      Gelezen
                    </button>
                  )}
                </div>
              </div>
              <IconButton icon={X} label="Wegklikken" onClick={() => dismiss.mutate(m.id)} className="-my-1.5 -mr-2 shrink-0" />
            </div>
          );
        })}
      </div>
    </Card>
  );
}
